import { Screen } from 'quasar'

/**
 * Keeps `$q.screen` on the same breakpoints as our stylesheets.
 *
 * Quasar's Screen plugin decides `$q.screen.gt.sm` and friends by comparing the viewport width
 * against thresholds in *pixels* (600/1024/1440/1920). Our stylesheets never use pixels: every
 * breakpoint goes through the `from()`/`to()` mixins, which run the size through `rem-fn()` and
 * emit `rem` — and `html { font-size: 100% }` ties `rem` to the browser's own font size setting.
 *
 * So a reader with Chrome's font size on "Small" (12px) gets the desktop grid from 768px while
 * `$q.screen` still reports `sm` until 1024px. Across that band the css lays out a desktop page
 * but every `v-if="$q.screen.gt.sm"` block stays unrendered, which is how the ARC feed lost its
 * filters sidebar and its group-by nav and left an empty left column [sc-27864].
 *
 * Pinning the stylesheets back to pixels would fix the mismatch but drop the font-size driven
 * reflow, so instead we hand Quasar the same rem thresholds the css uses, resolved against the
 * live root font size.
 *
 * Matching thresholds are not quite enough on their own: right on a breakpoint, Quasar's reading of
 * the width is a fraction of a pixel out and can land on the other side of it from the css. So each
 * threshold also moves a pixel towards whichever side of it the css is on [sc-28798].
 */

type ScreenSizeName = 'sm' | 'md' | 'lg' | 'xl'
type ScreenSizes = Record<ScreenSizeName, number>
type BreakpointQueries = Record<ScreenSizeName, MediaQueryList>

/** `rem-fn()`'s `$context`: `rem-fn(1024)` emits `64rem`, so this is how we get back to rem. */
const PIXELS_PER_REM = 16

/** The size the rem scale is authored against, and our fallback when the document can't be read. */
const DEFAULT_ROOT_FONT_SIZE = 16

/** Quasar's own size names. There is no `xs` threshold — `xs` is simply "below `sm`". */
const SCREEN_SIZE_NAMES: ScreenSizeName[] = ['sm', 'md', 'lg', 'xl']

/**
 * Quasar's shipped thresholds, snapshotted at import time — before anything here can scale
 * `Screen.sizes` — so re-syncing never scales an already scaled value.
 */
const DEFAULT_PIXEL_SIZES: ScreenSizes = { ...Screen.sizes } as ScreenSizes

/**
 * How far Quasar's reading of the viewport width can be from the width the media queries see. It
 * measures `visualViewport.width + innerWidth - clientWidth`, and the last two are whole pixels, so
 * with a fractional device pixel ratio and a scrollbar it lands a fraction of a pixel off, either
 * way: 1023.84 against the media queries' 1024 at Windows 125% and Chrome 125% on a 1600px window,
 * which put `$q.screen` on `sm` beside a desktop stylesheet [sc-28798]. The error is the difference
 * between how those two were rounded, so it never reaches a whole pixel.
 */
const QUASAR_WIDTH_ERROR = 1

const noop = () => { /* nothing to tear down */ }

/** A bare number or an explicit `px`. Anything else is a unit we can't treat as css pixels. */
const PIXEL_VALUE = /^[\d.]+(px)?$/

const isPositiveNumber = (value: number) => !Number.isNaN(value) && value > 0

/**
 * Only px means anything for a breakpoint we are about to scale by the root font size: a `rem`
 * value is already relative to it, so reading one as pixels would apply the font size twice.
 */
const parsePixels = (value: string): number => {
  const trimmed = value.trim()

  return PIXEL_VALUE.test(trimmed) ? parseFloat(trimmed) : NaN
}

const canReadDocument = () => !process.env.SERVER &&
  typeof window !== 'undefined' &&
  typeof window.getComputedStyle === 'function'

const getRootStyle = () => window.getComputedStyle(document.documentElement)

/**
 * The pixel thresholds the stylesheets were authored against. Read back out of the `--q-size-*`
 * custom properties Quasar generates from the `$sizes` sass map — the same map the mixins take
 * their `$breakpoint-*-min` values from — so this can't drift from the css. Falls back to Quasar's
 * defaults where a property is missing, which is every property under jsdom.
 */
const getAuthoredPixelSizes = (rootStyle: CSSStyleDeclaration): ScreenSizes => {
  const sizes = { ...DEFAULT_PIXEL_SIZES }

  for (const name of SCREEN_SIZE_NAMES) {
    const declared = parsePixels(rootStyle.getPropertyValue(`--q-size-${name}`))

    if (isPositiveNumber(declared)) {
      sizes[name] = declared
    }
  }

  return sizes
}

const getRootFontSize = (rootStyle: CSSStyleDeclaration): number => {
  const fontSize = parseFloat(rootStyle.fontSize)

  return isPositiveNumber(fontSize) ? fontSize : DEFAULT_ROOT_FONT_SIZE
}

/** What `from()` compiles a breakpoint to: `from(1024)` is `min-width: 64rem`. */
const getBreakpointQuery = (pixels: number) => `(min-width: ${pixels / PIXELS_PER_REM}rem)`

/** One media query per breakpoint, or none where the browser can't evaluate them. */
const getBreakpointQueries = (authored: ScreenSizes): BreakpointQueries | undefined => {
  if (typeof window.matchMedia !== 'function') {
    return void 0
  }

  const queries = {} as BreakpointQueries

  for (const name of SCREEN_SIZE_NAMES) {
    queries[name] = window.matchMedia(getBreakpointQuery(authored[name]))
  }

  return queries
}

/**
 * Where to put each of Quasar's thresholds, in css pixels.
 *
 * Each starts from its breakpoint: the width at which `min-width: 64rem` starts matching, left
 * unrounded so it is the one the browser compares the media query against. Quasar, though,
 * compares its own reading of the width, which can be a fraction of a pixel out, so right on a
 * breakpoint it could still land on the other side of it from the css. Where there is a media
 * query to ask, the threshold therefore moves a pixel away from the side the css is on — below
 * the breakpoint while the query matches, above it while it doesn't — which is further than
 * Quasar is ever out.
 */
const getScreenSizes = (rootStyle: CSSStyleDeclaration, queries?: BreakpointQueries): ScreenSizes => {
  const authored = getAuthoredPixelSizes(rootStyle)
  const rootFontSize = getRootFontSize(rootStyle)
  const sizes = {} as ScreenSizes

  for (const name of SCREEN_SIZE_NAMES) {
    const breakpoint = (authored[name] / PIXELS_PER_REM) * rootFontSize

    if (queries === void 0) {
      sizes[name] = breakpoint
    } else {
      sizes[name] = queries[name].matches
        ? breakpoint - QUASAR_WIDTH_ERROR
        : breakpoint + QUASAR_WIDTH_ERROR
    }
  }

  return sizes
}

const listen = (query: MediaQueryList, onChange: () => void): (() => void) => {
  if (typeof query.addEventListener === 'function') {
    query.addEventListener('change', onChange)

    return () => query.removeEventListener('change', onChange)
  }

  // Safari < 14 and the older webviews we still ship to only have the deprecated api.
  query.addListener(onChange)

  return () => query.removeListener(onChange)
}

const applyScreenSizes = (queries?: BreakpointQueries) => {
  Screen.setSizes(getScreenSizes(getRootStyle(), queries))
}

/**
 * Point Quasar's thresholds at the widths the css is currently using. Safe to call repeatedly;
 * after the Screen plugin's client takeover each call also re-evaluates `$q.screen`.
 */
export const syncScreenBreakpoints = (): void => {
  if (!canReadDocument()) {
    return
  }

  applyScreenSizes(getBreakpointQueries(getAuthoredPixelSizes(getRootStyle())))
}

/**
 * Sync now and stay in sync, returning a teardown.
 *
 * The watch is a media query per breakpoint rather than a resize listener because changing the
 * browser's font size moves the breakpoints without resizing the window — it fires no `resize`,
 * so Quasar's own listener never hears about it. A rem query changes state on both, and only for
 * a font size change that actually moves a breakpoint we care about. The same queries tell each
 * sync which side of its breakpoint the css is on, so that one listener is also what moves a
 * threshold across as the window is resized past it.
 */
export const installScreenBreakpointSync = (): (() => void) => {
  if (!canReadDocument()) {
    return noop
  }

  const queries = getBreakpointQueries(getAuthoredPixelSizes(getRootStyle()))
  const sync = () => applyScreenSizes(queries)

  sync()

  if (queries === void 0) {
    return noop
  }

  const teardowns = SCREEN_SIZE_NAMES.map(name => listen(queries[name], sync))

  return () => teardowns.forEach(teardown => teardown())
}
